import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Image,
  Modal,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  Animated,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import * as ImagePicker from 'expo-image-picker';
import { LinearGradient } from 'expo-linear-gradient';

const { width, height } = Dimensions.get('window');


const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const getH = async () => {
  const t = await AsyncStorage.getItem('auth_token').catch(() => null)
           || await AsyncStorage.getItem('token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': API_KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

export default function AttendanceScreen({ navigation, route }) {
  // Employee context from route params
  const routeEmployee = route?.params?.employee || null;
  const empId = route?.params?.employeeId
             || routeEmployee?.serverId
             || routeEmployee?.id
             || routeEmployee?.employeeId
             || null;
  const empName = routeEmployee?.name || routeEmployee?.firstName
               ? `${routeEmployee?.firstName || ''} ${routeEmployee?.lastName || ''}`.trim()
               : null;

  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState(routeEmployee);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [attendanceStatus, setAttendanceStatus] = useState('out'); // 'in', 'out', 'break'
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({});

  // Location and verification
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isWithinGeofence, setIsWithinGeofence] = useState(false);
  const [locationPermission, setLocationPermission] = useState(false);
  const [cameraPermission, setCameraPermission] = useState(false);

  // UI state
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Animation
  const [pulseAnim] = useState(new Animated.Value(1));
  const [clockButtonScale] = useState(new Animated.Value(1));

  // Office location (configurable)
  const officeLocation = useMemo(() => ({
    latitude: 51.5074, // London coordinates - replace with actual office
    longitude: -0.1278,
    radius: 100, // 100 meters radius
  }), []);

  // Initialize component
  useEffect(() => {
    initializeAttendance();
    requestPermissions();
    startTimeUpdater();
    startPulseAnimation();
  }, []);

  // Time updater
  const startTimeUpdater = useCallback(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Pulse animation for clock button
  const startPulseAnimation = useCallback(() => {
    const pulse = () => {
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]).start(() => pulse());
    };
    pulse();
  }, [pulseAnim]);

  // Request permissions
  const requestPermissions = useCallback(async () => {
    try {
      // Location permission
      const { status: locationStatus } = await Location.requestForegroundPermissionsAsync();
      setLocationPermission(locationStatus === 'granted');

      // Camera permission
      const { status: cameraStatus } = await ImagePicker.requestCameraPermissionsAsync();
      setCameraPermission(cameraStatus === 'granted');

      if (locationStatus === 'granted') {
        getCurrentLocation();
      }
    } catch (error) {
      console.error('Permission request error:', error);
    }
  }, []);

  // Get current location
  const getCurrentLocation = useCallback(async () => {
    try {
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });
      
      setCurrentLocation(location.coords);
      checkGeofence(location.coords);
    } catch (error) {
      console.error('Location error:', error);
      Alert.alert('Location Error', 'Unable to get your current location. Please check your GPS settings.');
    }
  }, []);

  // Check if user is within office geofence
  const checkGeofence = useCallback((coords) => {
    if (!coords) return;

    const distance = calculateDistance(
      coords.latitude,
      coords.longitude,
      officeLocation.latitude,
      officeLocation.longitude
    );

    setIsWithinGeofence(distance <= officeLocation.radius);
  }, [officeLocation]);

  // Calculate distance between two coordinates
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }, []);

  // Initialize attendance data
  const initializeAttendance = useCallback(async () => {
    try {
      setIsLoading(true);

      // Try loading today's attendance from server first
      const today = new Date().toISOString().split('T')[0];
      const id = empId || await AsyncStorage.getItem('employee_id').catch(() => null) || '0';
      try {
        const h = await getH();
        const res = await fetch(`${API_BASE}/api/data/attendance_records?employee_id=${parseInt(id)||0}&limit=30`, { headers: h });
        const json = await res.json().catch(() => ({}));
        const records = Array.isArray(json?.data) ? json.data : [];
        const todayRecord = records.find(r => (r.attendance_date || '').startsWith(today));
        if (todayRecord) {
          const serverData = {
            date: today,
            clockIn:  todayRecord.check_in   || null,
            clockOut: todayRecord.check_out  || null,
            totalBreakTime: 0,
            location: null,
            photos: {},
            _serverClockIn:  !!todayRecord.check_in,
            _serverClockOut: !!todayRecord.check_out,
          };
          await AsyncStorage.setItem(`attendance_${empId||"self"}_${today}`, JSON.stringify(serverData));
          console.log('[Attendance] Loaded today from server');
        }
        // Build weekly data from server records
        if (records.length > 0) {
          const serverWeekly = records.slice(0, 7).map(r => ({
            date: r.attendance_date,
            day: new Date(r.attendance_date).toLocaleDateString('en-GB', { weekday: 'short' }),
            data: { clockIn: r.check_in, clockOut: r.check_out, totalBreakTime: 0 },
          }));
          // store for later use by loadWeeklyAttendance
          await AsyncStorage.setItem('attendance_server_weekly', JSON.stringify(serverWeekly));
        }
      } catch (serverErr) {
        console.warn('[Attendance] Server load failed, using cache:', serverErr.message);
      }

      // Load today's attendance (now from cache, possibly server-updated)
      const todayData = await AsyncStorage.getItem(`attendance_${empId||"self"}_${today}`);
      
      if (todayData) {
        const parsed = JSON.parse(todayData);
        setTodayAttendance(parsed);
        
        // Determine current status
        if (parsed.clockOut) {
          setAttendanceStatus('out');
        } else if (parsed.breakStart && !parsed.breakEnd) {
          setAttendanceStatus('break');
        } else if (parsed.clockIn) {
          setAttendanceStatus('in');
        }
      }

      // Load weekly data
      await loadWeeklyAttendance();
      await loadMonthlyStats();

    } catch (error) {
      console.error('Initialize attendance error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load weekly attendance
  const loadWeeklyAttendance = useCallback(async () => {
    try {
      const weekData = [];
      const today = new Date();
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        const dayData = await AsyncStorage.getItem(`attendance_${dateStr}`);
        weekData.push({
          date: dateStr,
          day: date.toLocaleDateString('en-US', { weekday: 'short' }),
          data: dayData ? JSON.parse(dayData) : null,
        });
      }
      
      setWeeklyAttendance(weekData);
    } catch (error) {
      console.error('Load weekly attendance error:', error);
    }
  }, []);

  // Load monthly stats
  const loadMonthlyStats = useCallback(async () => {
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      let totalDays = 0;
      let presentDays = 0;
      let totalHours = 0;
      let lateCount = 0;
      
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const dayData = await AsyncStorage.getItem(`attendance_${dateStr}`);
        
        if (dayData) {
          const parsed = JSON.parse(dayData);
          totalDays++;
          
          if (parsed.clockIn) {
            presentDays++;
            
            if (parsed.clockOut) {
              const clockIn = new Date(parsed.clockIn);
              const clockOut = new Date(parsed.clockOut);
              const hours = (clockOut - clockIn) / (1000 * 60 * 60);
              totalHours += hours;
            }
            
            // Check if late (after 9 AM)
            const clockInTime = new Date(parsed.clockIn);
            if (clockInTime.getHours() > 9 || (clockInTime.getHours() === 9 && clockInTime.getMinutes() > 0)) {
              lateCount++;
            }
          }
        }
      }
      
      setMonthlyStats({
        totalDays,
        presentDays,
        totalHours: Math.round(totalHours * 10) / 10,
        lateCount,
        attendanceRate: totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0,
      });
    } catch (error) {
      console.error('Load monthly stats error:', error);
    }
  }, []);

  // Handle clock in/out
  const handleClockAction = useCallback(async (action) => {
    if (!locationPermission) {
      Alert.alert('Permission Required', 'Location permission is required for attendance tracking.');
      return;
    }

    if (!isWithinGeofence) {
      Alert.alert(
        'Location Verification',
        'You must be within the office premises to clock in/out. Current distance from office exceeds the allowed radius.',
        [
          { text: 'Retry', onPress: getCurrentLocation },
          { text: 'Cancel', style: 'cancel' },
        ]
      );
      return;
    }

    // Animate button press
    Animated.sequence([
      Animated.timing(clockButtonScale, {
        toValue: 0.95,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(clockButtonScale, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();

    // Show photo capture modal
    setShowPhotoModal(true);
  }, [locationPermission, isWithinGeofence, getCurrentLocation, clockButtonScale]);

  // Capture photo for verification
  const capturePhoto = useCallback(async () => {
    try {
      if (!cameraPermission) {
        Alert.alert('Permission Required', 'Camera permission is required for photo verification.');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setCapturedPhoto(result.assets[0].uri);
        return result.assets[0].uri;
      }
    } catch (error) {
      console.error('Photo capture error:', error);
      Alert.alert('Error', 'Failed to capture photo. Please try again.');
    }
    return null;
  }, [cameraPermission]);

  // Process attendance action
  const processAttendance = useCallback(async (action, photoUri) => {
    try {
      setIsProcessing(true);
      
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const timestamp = now.toISOString();
      
      // Get existing attendance data
      let attendanceData = todayAttendance || {
        date: today,
        clockIn: null,
        clockOut: null,
        breakStart: null,
        breakEnd: null,
        totalBreakTime: 0,
        location: null,
        photos: {},
        notes: '',
      };

      // Update based on action
      switch (action) {
        case 'clockIn':
          attendanceData.clockIn = timestamp;
          attendanceData.location = currentLocation;
          attendanceData.photos.clockIn = photoUri;
          setAttendanceStatus('in');
          break;
          
        case 'clockOut':
          attendanceData.clockOut = timestamp;
          attendanceData.photos.clockOut = photoUri;
          setAttendanceStatus('out');
          break;
          
        case 'breakStart':
          attendanceData.breakStart = timestamp;
          attendanceData.photos.breakStart = photoUri;
          setAttendanceStatus('break');
          break;
          
        case 'breakEnd':
          attendanceData.breakEnd = timestamp;
          attendanceData.photos.breakEnd = photoUri;
          
          // Calculate break time
          if (attendanceData.breakStart) {
            const breakDuration = (new Date(timestamp) - new Date(attendanceData.breakStart)) / (1000 * 60);
            attendanceData.totalBreakTime += breakDuration;
          }
          
          setAttendanceStatus('in');
          break;
      }

      // Save to AsyncStorage
      await AsyncStorage.setItem(`attendance_${empId||"self"}_${today}`, JSON.stringify(attendanceData));
      setTodayAttendance(attendanceData);

      // Sync to server (if online)
      await syncAttendanceToServer(attendanceData);

      // Show success message
      const actionMessages = {
        clockIn: 'Clocked in successfully!',
        clockOut: 'Clocked out successfully!',
        breakStart: 'Break started!',
        breakEnd: 'Break ended!',
      };
      
      Alert.alert('Success', actionMessages[action]);
      
      // Refresh data
      await loadWeeklyAttendance();
      await loadMonthlyStats();

    } catch (error) {
      console.error('Process attendance error:', error);
      Alert.alert('Error', 'Failed to record attendance. Please try again.');
    } finally {
      setIsProcessing(false);
      setShowPhotoModal(false);
      setCapturedPhoto(null);
    }
  }, [todayAttendance, currentLocation, loadWeeklyAttendance, loadMonthlyStats]);

  // Sync attendance to server — uses real payroll routes API
  const syncAttendanceToServer = useCallback(async (attendanceData) => {
    try {
      const h = await getH();
      const id = empId || await AsyncStorage.getItem('employee_id').catch(() => null) || '0';

      if (attendanceData.clockIn && !attendanceData._serverClockIn) {
        const res = await fetch(`${API_BASE}/api/attendance/check-in`, {
          method: 'POST', headers: h,
          body: JSON.stringify({ employeeId: id, timestamp: attendanceData.clockIn, location: attendanceData.location }),
        });
        const json = await res.json().catch(() => ({}));
        if (json.success || res.ok) {
          attendanceData._serverClockIn = true;
          console.log('[Attendance] Checked in on server');
        }
      }

      if (attendanceData.clockOut && !attendanceData._serverClockOut) {
        const res = await fetch(`${API_BASE}/api/attendance/check-out`, {
          method: 'POST', headers: h,
          body: JSON.stringify({ employeeId: id, timestamp: attendanceData.clockOut }),
        });
        const json = await res.json().catch(() => ({}));
        if (json.success || res.ok) {
          attendanceData._serverClockOut = true;
          console.log('[Attendance] Checked out on server');
        }
      }

      // Also save to generic attendance_records table
      const today = new Date().toISOString().split('T')[0];
      await fetch(`${API_BASE}/api/data/attendance_records`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          employee_id: parseInt(id) || 0,
          attendance_date: today,
          check_in:    attendanceData.clockIn    || null,
          check_out:   attendanceData.clockOut   || null,
          hours_worked: attendanceData.clockIn && attendanceData.clockOut
            ? ((new Date(attendanceData.clockOut) - new Date(attendanceData.clockIn)) / 3600000 - (attendanceData.totalBreakTime || 0) / 60).toFixed(2)
            : null,
          status: attendanceData.clockIn ? 'present' : 'absent',
        }),
      }).catch(() => {});

      // Update shared EmployeeDashboard keys
      const checkedIn = attendanceData.clockIn && !attendanceData.clockOut;
      await AsyncStorage.setItem('employee_checked_in', String(checkedIn));
      if (attendanceData.clockIn) await AsyncStorage.setItem('employee_last_checkin', attendanceData.clockIn);

    } catch (error) {
      console.error('[Attendance] Sync error:', error.message);
      // Queue for later
      const pending = JSON.parse(await AsyncStorage.getItem('pending_attendance_sync').catch(() => '[]') || '[]');
      pending.push(attendanceData);
      await AsyncStorage.setItem('pending_attendance_sync', JSON.stringify(pending));
    }
  }, [empId]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await getCurrentLocation();
      await initializeAttendance();
    } catch (error) {
      console.error('Refresh error:', error);
    } finally {
      setRefreshing(false);
    }
  }, [getCurrentLocation, initializeAttendance]);

  // Get current status display
  const getStatusDisplay = useCallback(() => {
    switch (attendanceStatus) {
      case 'in':
        return {
          text: 'Clocked In',
          color: '#10B981',
          icon: 'checkmark-circle',
          action: 'clockOut',
          actionText: 'Clock Out',
          actionColor: '#EF4444',
        };
      case 'out':
        return {
          text: 'Clocked Out',
          color: '#64748B',
          icon: 'time-outline',
          action: 'clockIn',
          actionText: 'Clock In',
          actionColor: '#10B981',
        };
      case 'break':
        return {
          text: 'On Break',
          color: '#F59E0B',
          icon: 'pause-circle',
          action: 'breakEnd',
          actionText: 'End Break',
          actionColor: '#10B981',
        };
      default:
        return {
          text: 'Unknown',
          color: '#64748B',
          icon: 'help-circle',
          action: 'clockIn',
          actionText: 'Clock In',
          actionColor: '#10B981',
        };
    }
  }, [attendanceStatus]);

  // Format time
  const formatTime = useCallback((date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }, []);

  // Format date
  const formatDate = useCallback((date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  // Calculate working hours
  const calculateWorkingHours = useCallback((attendance) => {
    if (!attendance || !attendance.clockIn) return '0h 0m';
    
    const clockIn = new Date(attendance.clockIn);
    const clockOut = attendance.clockOut ? new Date(attendance.clockOut) : new Date();
    
    let totalMinutes = (clockOut - clockIn) / (1000 * 60);
    totalMinutes -= attendance.totalBreakTime || 0;
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    
    return `${hours}h ${minutes}m`;
  }, []);

  const statusDisplay = getStatusDisplay();

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Attendance...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient
        colors={['#4A90E2', '#357ABD']}
        style={styles.header}
      >
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={{ alignItems:'center' }}>
          <Text style={styles.headerTitle}>Attendance</Text>
          {empName ? <Text style={{ color:'rgba(255,255,255,0.75)', fontSize:11, marginTop:1 }}>{empName}</Text> : null}
        </View>
        
        <View style={{ flexDirection:'row', gap:8 }}>
          <TouchableOpacity style={styles.historyButton} onPress={() => navigation.navigate('Timesheet', { employeeId: empId, employee: routeEmployee })}>
            <MaterialCommunityIcons name="calendar-clock" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.historyButton} onPress={() => setShowHistoryModal(true)}>
            <MaterialCommunityIcons name="history" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#4A90E2']}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Current Time & Date */}
        <View style={styles.timeSection}>
          <Text style={styles.currentTime}>{formatTime(currentTime)}</Text>
          <Text style={styles.currentDate}>{formatDate(currentTime)}</Text>
        </View>

        {/* Status Card */}
        <View style={styles.statusCard}>
          <View style={styles.statusHeader}>
            <View style={styles.statusIndicator}>
              <Ionicons name={statusDisplay.icon} size={24} color={statusDisplay.color} />
              <Text style={[styles.statusText, { color: statusDisplay.color }]}>
                {statusDisplay.text}
              </Text>
            </View>
            
            {/* Location Status */}
            <View style={styles.locationStatus}>
              <Ionicons 
                name={isWithinGeofence ? "location" : "location-outline"} 
                size={16} 
                color={isWithinGeofence ? "#10B981" : "#EF4444"} 
              />
              <Text style={[
                styles.locationText,
                { color: isWithinGeofence ? "#10B981" : "#EF4444" }
              ]}>
                <Text>{isWithinGeofence ? "In Office" : "Outside Office"}</Text>
              </Text>
            </View>
          </View>

          {/* Today's Summary */}
          {todayAttendance && (
            <View style={styles.todaySummary}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Clock In</Text>
                <Text style={styles.summaryValue}>
                  {todayAttendance.clockIn ? 
                    formatTime(new Date(todayAttendance.clockIn)) : '--:--'
                  }
                </Text>
              </View>
              
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Clock Out</Text>
                <Text style={styles.summaryValue}>
                  {todayAttendance.clockOut ? 
                    formatTime(new Date(todayAttendance.clockOut)) : '--:--'
                  }
                </Text>
              </View>
              
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Working Hours</Text>
                <Text style={styles.summaryValue}>
                  {calculateWorkingHours(todayAttendance)}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* Clock Action Button */}
        <Animated.View style={[
          styles.clockButtonContainer,
          { transform: [{ scale: clockButtonScale }] }
        ]}>
          <TouchableOpacity
            style={[styles.clockButton, { backgroundColor: statusDisplay.actionColor }]}
            onPress={() => handleClockAction(statusDisplay.action)}
            disabled={isProcessing || !locationPermission}
          >
            <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
              <MaterialCommunityIcons 
                name="clock-outline" 
                size={48} 
                color="#FFFFFF" 
              />
            </Animated.View>
            <Text style={styles.clockButtonText}>{statusDisplay.actionText}</Text>
          </TouchableOpacity>
        </Animated.View>

        {/* Break Button */}
        {attendanceStatus === 'in' && (
          <TouchableOpacity
            style={styles.breakButton}
            onPress={() => handleClockAction('breakStart')}
            disabled={isProcessing}
          >
            <MaterialCommunityIcons name="coffee" size={24} color="#F59E0B" />
            <Text style={styles.breakButtonText}>Start Break</Text>
          </TouchableOpacity>
        )}

        {/* Weekly Overview */}
        <View style={styles.weeklySection}>
          <Text style={styles.sectionTitle}>This Week</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.weeklyContainer}>
              {weeklyAttendance.map((day, index) => (
                <View key={index} style={styles.dayCard}>
                  <Text style={styles.dayLabel}>{day.day}</Text>
                  <View style={[
                    styles.dayStatus,
                    { backgroundColor: day.data?.clockIn ? '#10B981' : '#E2E8F0' }
                  ]}>
                    <Ionicons 
                      name={day.data?.clockIn ? "checkmark" : "close"} 
                      size={16} 
                      color={day.data?.clockIn ? "#FFFFFF" : "#64748B"} 
                    />
                  </View>
                  <Text style={styles.dayTime}>
                    {day.data?.clockIn ? 
                      formatTime(new Date(day.data.clockIn)).slice(0, -3) : '--:--'
                    }
                  </Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </View>

        {/* Monthly Stats */}
        <View style={styles.statsSection}>
          <Text style={styles.sectionTitle}>Monthly Statistics</Text>
          <View style={styles.statsGrid}>
            <View style={styles.statCard}>
              <MaterialCommunityIcons name="calendar-check" size={32} color="#10B981" />
              <Text style={styles.statValue}>{monthlyStats.presentDays || 0}</Text>
              <Text style={styles.statLabel}>Present Days</Text>
            </View>
            
            <View style={styles.statCard}>
              <MaterialCommunityIcons name="clock-outline" size={32} color="#4A90E2" />
              <Text style={styles.statValue}>{monthlyStats.totalHours || 0}h</Text>
              <Text style={styles.statLabel}>Total Hours</Text>
            </View>
            
            <View style={styles.statCard}>
              <MaterialCommunityIcons name="percent" size={32} color="#059669" />
              <Text style={styles.statValue}>{monthlyStats.attendanceRate || 0}%</Text>
              <Text style={styles.statLabel}>Attendance</Text>
            </View>
            
            <View style={styles.statCard}>
              <MaterialCommunityIcons name="clock-alert" size={32} color="#EF4444" />
              <Text style={styles.statValue}>{monthlyStats.lateCount || 0}</Text>
              <Text style={styles.statLabel}>Late Days</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Photo Capture Modal */}
      <Modal
        visible={showPhotoModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowPhotoModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Photo Verification</Text>
            <TouchableOpacity onPress={() => setShowPhotoModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <View style={styles.photoModalContent}>
            <MaterialCommunityIcons name="camera" size={64} color="#4A90E2" />
            <Text style={styles.photoModalTitle}>Take a Photo</Text>
            <Text style={styles.photoModalDescription}>
              Please take a photo for attendance verification
            </Text>
            
            {capturedPhoto && (
              <Image source={{ uri: capturedPhoto }} style={styles.capturedPhoto} />
            )}
            
            <View style={styles.photoModalButtons}>
              <TouchableOpacity
                style={styles.captureButton}
                onPress={capturePhoto}
                disabled={isProcessing}
              >
                <MaterialCommunityIcons name="camera" size={24} color="#FFFFFF" />
                <Text style={styles.captureButtonText}>
                  <Text>{capturedPhoto ? 'Retake Photo' : 'Take Photo'}</Text>
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity style={[styles.confirmButton, { backgroundColor:'#64748B', marginTop:8 }]}
                onPress={() => processAttendance(statusDisplay.action, null)}>
                <MaterialCommunityIcons name="check" size={20} color="#FFFFFF" />
                <Text style={styles.confirmButtonText}>Continue Without Photo</Text>
            </TouchableOpacity>

            {capturedPhoto && (
                <TouchableOpacity
                  style={styles.confirmButton}
                  onPress={() => processAttendance(statusDisplay.action, capturedPhoto)}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <ActivityIndicator size={20} color="#FFFFFF" />
                  ) : (
                    <>
                      <MaterialCommunityIcons name="check" size={24} color="#FFFFFF" />
                      <Text style={styles.confirmButtonText}>Confirm</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
            </View>
          </View>
        </SafeAreaView>
      </Modal>

      {/* History Modal */}
      <Modal
        visible={showHistoryModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowHistoryModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Attendance History</Text>
            <TouchableOpacity onPress={() => setShowHistoryModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.historyContent}>
            {weeklyAttendance.map((day, index) => (
              <View key={index} style={styles.historyItem}>
                <View style={styles.historyDate}>
                  <Text style={styles.historyDateText}>
                    {new Date(day.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Text>
                </View>
                
                <View style={styles.historyDetails}>
                  {day.data ? (
                    <>
                      <View style={styles.historyTime}>
                        <Text style={styles.historyTimeLabel}>In:</Text>
                        <Text style={styles.historyTimeValue}>
                          {day.data.clockIn ? 
                            formatTime(new Date(day.data.clockIn)) : '--:--'
                          }
                        </Text>
                      </View>
                      
                      <View style={styles.historyTime}>
                        <Text style={styles.historyTimeLabel}>Out:</Text>
                        <Text style={styles.historyTimeValue}>
                          {day.data.clockOut ? 
                            formatTime(new Date(day.data.clockOut)) : '--:--'
                          }
                        </Text>
                      </View>
                      
                      <View style={styles.historyTime}>
                        <Text style={styles.historyTimeLabel}>Hours:</Text>
                        <Text style={styles.historyTimeValue}>
                          {calculateWorkingHours(day.data)}
                        </Text>
                      </View>
                    </>
                  ) : (
                    <Text style={styles.historyAbsent}>No attendance recorded</Text>
                  )}
                </View>
                
                <View style={styles.historyStatus}>
                  <Ionicons 
                    name={day.data?.clockIn ? "checkmark-circle" : "close-circle"} 
                    size={24} 
                    color={day.data?.clockIn ? "#10B981" : "#EF4444"} 
                  />
                </View>
              </View>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  historyButton: {
    padding: 8,
  },
  content: {
    flex: 1,
  },
  timeSection: {
    alignItems: 'center',
    paddingVertical: 32,
    backgroundColor: '#FFFFFF',
  },
  currentTime: {
    fontSize: 48,
    fontWeight: '300',
    color: '#0F172A',
    fontFamily: Platform.OS === 'ios' ? 'Helvetica Neue' : 'Roboto',
  },
  currentDate: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 8,
  },
  statusCard: {
    margin: 20,
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statusHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  statusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusText: {
    fontSize: 18,
    fontWeight: '600',
    marginLeft: 8,
  },
  locationStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  locationText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  todaySummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  clockButtonContainer: {
    alignItems: 'center',
    marginVertical: 32,
  },
  clockButton: {
    width: 160,
    height: 160,
    borderRadius: 80,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  clockButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 8,
  },
  breakButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 20,
    marginBottom: 32,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#F59E0B',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  breakButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F59E0B',
    marginLeft: 8,
  },
  weeklySection: {
    marginHorizontal: 20,
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  weeklyContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  dayCard: {
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    minWidth: 80,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  dayLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 8,
  },
  dayStatus: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  dayTime: {
    fontSize: 12,
    color: '#0F172A',
    fontWeight: '500',
  },
  statsSection: {
    marginHorizontal: 20,
    marginBottom: 32,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: (width - 56) / 2,
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 8,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
  },
  photoModalContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  photoModalTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  photoModalDescription: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 32,
  },
  capturedPhoto: {
    width: 200,
    height: 200,
    borderRadius: 12,
    marginBottom: 32,
  },
  photoModalButtons: {
    width: '100%',
    gap: 16,
  },
  captureButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    backgroundColor: '#4A90E2',
    borderRadius: 12,
  },
  captureButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  confirmButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    backgroundColor: '#10B981',
    borderRadius: 12,
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  historyContent: {
    padding: 20,
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  historyDate: {
    width: 80,
  },
  historyDateText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0F172A',
  },
  historyDetails: {
    flex: 1,
    paddingHorizontal: 16,
  },
  historyTime: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  historyTimeLabel: {
    fontSize: 12,
    color: '#64748B',
  },
  historyTimeValue: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0F172A',
  },
  historyAbsent: {
    fontSize: 14,
    color: '#64748B',
    fontStyle: 'italic',
  },
  historyStatus: {
    width: 40,
    alignItems: 'center',
  },
});